import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Box,
  useTheme,
  alpha,
  Divider,
  Avatar,
  Snackbar,
  Grid,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { fetchProfileDataAction } from '../../store/actions/profile';
import { getBankAccountOtp, addBankAccount } from '../../services/backendAPI';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';


const AddBankModal = ({ open, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [otpSent, setOtpSent] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);
  const [isIfscLoading, setIsIfscLoading] = useState(false);
  const [timer, setTimer] = useState(180);
  const [canResendOTP, setCanResendOTP] = useState(false);

  const [formData, setFormData] = useState({
    account_type: 'SAVINGS',
    bank_account_number: '',
    bank_name: '',
    city: '',
    ifsc: '',
    state: '',
    address: 'temp',
    otp: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) {
      handleSendOTP();
    } else {
      setOtpSent(false);
      setTimer(30);
      setCanResendOTP(false);
      setFormData({
        account_type: 'SAVINGS',
        bank_account_number: '',
        bank_name: '',
        city: '',
        ifsc: '',
        state: '',
        address: 'temp',
      });
    }
  }, [open]);

  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResendOTP(true);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  const handleSendOTP = async () => {
    setIsLoading(true);
    try {
      const response = await getBankAccountOtp();
      if (response.status === 'success') {
        setOtpSent(true);
        setSnackbarSeverity('success');
        setSnackbarMessage('OTP sent successfully');
        setTimer(180);
        setCanResendOTP(false);
      } else {
        setSnackbarSeverity('error');
        // setSnackbarMessage('Failed to send OTP. Please try again.');
      }
    } catch (error) {

      setSnackbarSeverity('error');
      setSnackbarMessage(error);
    } finally {
      setIsLoading(false);
      setShowSnackbar(true);
    }
  };

  // Add debounce function for IFSC lookup
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // IFSC lookup function
  const lookupIFSC = async (ifscCode) => {
    if (!ifscCode || ifscCode.length !== 11) return;

    setIsIfscLoading(true);
    try {
      const response = await axios.get(`https://ifsc.razorpay.com/${ifscCode}`);
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          bank_name: response.data.BANK,
          city:response.data.CITY,
          state:response.data.STATE,
        }));
        // Clear IFSC error if exists
        setErrors(prev => ({
          ...prev,
          ifsc: ''
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        ifsc: 'Invalid IFSC code'
      }));
    } finally {
      setIsIfscLoading(false);
    }
  };

  const debouncedLookupIFSC = React.useCallback(
    debounce(lookupIFSC, 500),
    []
  );

  // Modified handleChange function
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === 'ifsc') {
      updatedValue = value.toUpperCase();
      if (updatedValue.length === 11) {
        debouncedLookupIFSC(updatedValue);
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: updatedValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bank_name) {
      newErrors.bank_name = 'Bank name is required';
    }

    if (!formData.bank_account_number) {
      newErrors.bank_account_number = 'Account number is required';
    }

    if (!formData.ifsc) {
      newErrors.ifsc = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc)) {
      newErrors.ifsc = 'Invalid IFSC code';
    }

    if (!formData.city) {
      newErrors.city = 'City is required';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    if (!formData.otp) {
      newErrors.otp = 'OTP is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await addBankAccount({
        ...formData,
      });

      if (response.status === 'success') {
        dispatch(fetchProfileDataAction());
        enqueueSnackbar(response.message, { variant: 'success' });
        onClose();
      } else {
        setErrors(prev => ({
          ...prev,
          submit: response.message || 'Failed to add bank account'
        }));
      }
    } catch (error) {
      enqueueSnackbar(error, {
        variant: 'error', autoHideDuration: 1000, anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            maxWidth: 500,
            width: '100%',
            height: isMobile ? '100%' : 'auto',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          p: isMobile ? 2 : 3,
          background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          position: 'relative',
          pt: isMobile ? 'env(safe-area-inset-top)' : 3
        }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: 'radial-gradient(circle at 20px 20px, rgba(255,255,255,0.3) 2px, transparent 0)',
              backgroundSize: '40px 40px'
            }}
          />
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            position: 'relative',
            pt: isMobile ? 1 : 0
          }}>
            <Avatar
              sx={{
                width: isMobile ? 48 : 56,
                height: isMobile ? 48 : 56,
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(5px)'
              }}
            >
              <AccountBalanceIcon sx={{
                color: 'white',
                fontSize: isMobile ? 24 : 30
              }} />
            </Avatar>
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{
                color: 'white',
                fontWeight: 700,
                mb: 0.5
              }}>
                Add Bank Account
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Enter your bank account details
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 0,
                top: isMobile ? 0 : 0,
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <CloseIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{
          p: isMobile ? 2 : 3,
          flex: isMobile ? 1 : 'initial',
          overflow: 'auto'
        }}>
          <Stack spacing={isMobile ? 2.5 : 3}>
            {errors.submit && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                }}
              >
                {errors.submit}
              </Alert>
            )}

            {/* Account Type Selection */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary, fontWeight: 600 }}>
                Account Type
              </Typography>
              <FormControl>
                <RadioGroup row name="account_type" value={formData.account_type} onChange={handleChange} sx={{ gap: 2 }}>
                  <FormControlLabel
                    value="SAVINGS"
                    control={<Radio sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2, borderRadius: 2, border: `1px solid ${formData.account_type === 'SAVINGS' ? theme.palette.primary.main : theme.palette.divider}`, bgcolor: formData.account_type === 'SAVINGS' ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
                        <Typography sx={{ fontWeight: 500 }}>Savings Account</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="CURRENT"
                    control={<Radio sx={{ '&.Mui-checked': { color: theme.palette.primary.main } }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2, borderRadius: 2, border: `1px solid ${formData.account_type === 'CURRENT' ? theme.palette.primary.main : theme.palette.divider}`, bgcolor: formData.account_type === 'CURRENT' ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}>
                        <Typography sx={{ fontWeight: 500 }}>Current Account</Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Bank Information Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary, fontWeight: 600 }}>
                Bank Information
              </Typography>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  name="ifsc"
                  value={formData.ifsc}
                  onChange={handleChange}
                  error={Boolean(errors.ifsc)}
                  helperText={errors.ifsc || "Enter your bank's IFSC code to auto-fill bank details"}
                  placeholder="Enter IFSC code"
                  InputProps={{
                    sx: { borderRadius: 2 },
                    endAdornment: (
                      <InputAdornment position="end">
                        {isIfscLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => window.open('https://www.rbi.org.in/Scripts/IFSCMICRDetails.aspx', '_blank')}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          </IconButton>
                        )}
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  fullWidth
                  label="Bank Name"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  error={Boolean(errors.bank_name)}
                  helperText={errors.bank_name}
                  placeholder="Enter your bank name or use IFSC code above"
                  InputProps={{
                    sx: { borderRadius: 2 },
                    readOnly: Boolean(formData.ifsc && formData.bank_name),
                  }}
                />

                <TextField
                  fullWidth
                  label="Bank Account Number"
                  name="bank_account_number"
                  value={formData.bank_account_number}
                  onChange={handleChange}
                  error={Boolean(errors.bank_account_number)}
                  helperText={errors.bank_account_number}
                  placeholder="Enter your account number"
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />

                {/* Location Fields */}

              </Stack>
            </Box>

            {/* OTP Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary, fontWeight: 600 }}>
                Verification
              </Typography>
              <TextField
                fullWidth
                label="OTP"
                name="otp"
                value={formData.otp}
                onChange={handleChange}
                error={Boolean(errors.otp)}
                helperText={errors.otp}
                placeholder="Enter OTP"
                InputProps={{
                  sx: { borderRadius: 2 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {timer > 0 && otpSent && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              minWidth: 45
                            }}
                          >
                            {`${timer}s`}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          onClick={handleSendOTP}
                          disabled={isLoading || (otpSent && !canResendOTP)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            minWidth: 85,
                            ...(timer > 0 && otpSent && {
                              opacity: 0.7,
                              cursor: 'not-allowed'
                            })
                          }}
                        >
                          {otpSent ? 'Resend OTP' : 'Get OTP'}
                        </Button>
                      </Box>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: isMobile ? 2 : 3,
            pb: isMobile ? 'calc(env(safe-area-inset-bottom) + 16px)' : 3,
            pt: isMobile ? 2 : 3,
            gap: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            flexDirection: isMobile ? 'column' : 'row',
            '& > button': {
              flex: isMobile ? 1 : 'initial',
              width: isMobile ? '100%' : 'auto'
            }
          }}
        >
          <Button
            onClick={onClose}
            variant={isMobile ? "outlined" : "text"}
            size={isMobile ? "large" : "medium"}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              order: isMobile ? 2 : 1
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            size={isMobile ? "large" : "medium"}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
              },
              order: isMobile ? 1 : 2
            }}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Add Bank Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{
          vertical: isMobile ? 'bottom' : 'top',
          horizontal: isMobile ? 'center' : 'right'
        }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '400px'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddBankModal;